import React from 'react';
import { Tabs, TabsProps } from 'antd';

import TabTitle from '../tabs/TabTitle';

import './IBTabs.css';

export type IBTabItem = NonNullable<TabsProps['items']>[number] & {
  group?: string;
};

type Props = Omit<TabsProps, 'items'> & {
  items?: IBTabItem[];
};

type TabGroupCluster = {
  group?: string;
  items: IBTabItem[];
};

const clusterByGroup = (items: IBTabItem[]): TabGroupCluster[] => {
  const clusters: TabGroupCluster[] = [];

  items.forEach((item) => {
    const currentCluster = clusters[clusters.length - 1];

    if (currentCluster && currentCluster.group === item.group) {
      currentCluster.items.push(item);
    } else {
      clusters.push({ group: item.group, items: [item] });
    }
  });

  return clusters;
};

const IBTabs = ({ items, children, ...rest }: Props) => {
  const tabItems = items?.map((item) => ({
    ...item,
    label: <TabTitle>{item.label}</TabTitle>,
  }));

  const hasGroups = items?.some((item) => typeof item.group !== 'undefined');

  const renderGroupedTabBar: TabsProps['renderTabBar'] = (tabBarProps) => {
    const clusters = clusterByGroup(items ?? []);

    return (
      <div styleName="tab-group-bar" role="tablist">
        {clusters.map((cluster, clusterIndex) => (
          <div
            styleName="tab-group"
            key={cluster.group ?? `ungrouped-${clusterIndex}`}
          >
            {cluster.group && (
              <div styleName="tab-group-label">
                <span>{cluster.group}</span>
              </div>
            )}
            <div styleName="tab-group-items">
              {cluster.items.map((item) => (
                <div
                  key={item.key}
                  role="tab"
                  aria-selected={tabBarProps.activeKey === item.key}
                  aria-disabled={item.disabled}
                  styleName={`tab-group-item ${
                    tabBarProps.activeKey === item.key
                      ? 'tab-group-item-active'
                      : ''
                  } ${item.disabled ? 'tab-group-item-disabled' : ''}`}
                  onClick={(event) =>
                    !item.disabled &&
                    tabBarProps.onTabClick(item.key as string, event)
                  }
                >
                  <TabTitle>{item.label}</TabTitle>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    // @ts-ignore - AntD TabsProps onMouseEnter conflicts with Tabs' internal type
    <Tabs
      {...rest}
      items={tabItems}
      renderTabBar={hasGroups ? renderGroupedTabBar : undefined}
    >
      {children}
    </Tabs>
  );
};

export default IBTabs;
